<template>
    <div>
        <navigation @editPassword="editPassword"></navigation>
        <v-content>
            <v-container grid-list-md fluid>
                <router-view/>
            </v-container>
            <v-dialog v-model="isUnauthorized" max-width="290">
                <v-card>
                    <v-card-title class="headline">Unauthorized</v-card-title>
                    <v-card-text>
                        <v-layout row>
                            Please login again!
                        </v-layout>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn @click="reLogin" flat color="primary">OK</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-content>

        <v-dialog v-model="changePasswordDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title>
                    <span class="headline">Change Password</span>
                </v-card-title>
                <v-card-text>
                    <v-form 
                        ref="changePasswordForm"
                        lazy-validation
                    >
                        <v-container>
                            <v-layout column wrap>
                                <v-flex xs12 sm6 md4>
                                    <v-text-field 
                                        v-model="passwords.oldPassword" 
                                        label="Old Password" 
                                        required
                                        type="password"
                                        :rules="passwordRules.oldPasswordRule"
                                        :error-messages="passwordMismatch"
                                    ></v-text-field>
                                </v-flex>
                                <v-flex xs12 sm6 md4>
                                    <v-text-field 
                                        v-model="passwords.newPassword" 
                                        label="New Password" 
                                        required
                                        type="password"
                                        :rules="passwordRules.newPasswordRule"
                                    ></v-text-field>
                                </v-flex>
                                <v-flex xs12 sm6 md4>
                                    <v-text-field 
                                        v-model="passwords.confirmPassword" 
                                        label="Confirm Password" 
                                        required
                                        type="password"
                                        :rules="passwordRules.confirmPasswordRule"
                                    ></v-text-field>
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn flat @click="changePasswordDialog = false">Cancel</v-btn>
                    <v-btn flat @click="changePassword">Ok</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar
            v-model="passwordChangedMsg"
            top
        >
            Password changed successfully!
            <v-btn
                color="pink"
                flat
                @click="passwordChangedMsg = false"
            >
                Close
            </v-btn>
        </v-snackbar>
    </div>
</template>

<script>
    import Navigation from '../components/Navigation'
    import {TokenService} from "../services/token.services";
    import {APIService} from "../services/api.services";
    import {UserService} from "../services/user.services";
    import { mapActions, mapMutations, mapGetters } from "vuex";
    import current_user from '../mixins/current_user';

    export default {
        components: { Navigation },

        mixins: [ current_user ],

        data() {
            return {
                changePasswordDialog: false,
                passwords: {
                    oldPassword: "",
                    newPassword: "",
                    confirmPassword: ""
                },
                passwordMismatch: undefined,
                passwordChangedMsg: false
            }
        },

        created() {
            if (TokenService.getToken()) {
                APIService.setHeader()

                this.loadMedias();

                if (this.isSuperAdmin || this.isBindezAdmin) {
                    this.loadOrganizations();
                    this.fetchAdminDashboards();
                } else {
                    this.setOrganizations([ this.currentUser.organization ]);
                }
                
                if (!this.isScanningAdmin) {
                    this.loadDashboards();
                } else {
                    this.setDashboards([]);
                    this.setAdminDashboards([]);
                }
            }
        },

        methods: {
            ...mapActions('medias', ['fetchMedias']),

            ...mapMutations('medias', ['setMediaLoading']),

            ...mapActions('dashboards', [
                'fetchDashboards',
                'fetchAdminDashboards'
            ]),

            ...mapMutations('dashboards', [ 'setDashboardLoading', 'setDashboards', 'setAdminDashboards' ]),

            ...mapActions('organizations', ['fetchOrganizations']),

            ...mapMutations('organizations', ['setOrganizationLoading', 'setOrganizations']),

            loadMedias() {
                this.setMediaLoading(true);
                this.fetchMedias()
                    .finally(() => this.setMediaLoading(false));
            },

            loadOrganizations() {
                this.setOrganizationLoading(true);
                this.fetchOrganizations()
                    .finally(() => this.setOrganizationLoading(false));
            },

            loadDashboards() {
                this.setDashboardLoading(true);
                this.fetchDashboards()
                    .finally(() => this.setDashboardLoading(false));
            },

            reLogin() {
                UserService.logout();
                this.$router.push('/login');
            },

            editPassword() {
                Object.assign(this.passwords, {
                    oldPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                this.passwordMismatch = undefined;
                this.changePasswordDialog = true;
            },

            changePassword() {
                const valid = this.$refs.changePasswordForm.validate();
                if (valid) {
                    this.changeUserPassword({
                        oldPassword: this.passwords.oldPassword,
                        newPassword: this.passwords.newPassword
                    })
                    .then(() => {
                        this.changePasswordDialog = false;
                        this.passwordChangedMsg = true;
                    })
                    .catch(({ response }) => {
                        if (response.status === 401)
                            this.passwordMismatch = "Passwords Mismatch";
                    });
                }
            },

            ...mapActions([ 'changeUserPassword' ]),
        },

        computed: {
            ...mapGetters(
                [ "isUnauthorized" ]
            ),

            passwordRules() {
                return {
                    oldPasswordRule: [ 
                        v => !!v || 'Old Password is required'
                    ],
                    newPasswordRule: [ v => !!v || 'New Password is required' ],
                    confirmPasswordRule: [ 
                        v => !!v || 'Confirm Password is required',
                        v => this.passwords.newPassword === v || 'Passwords do not match'
                    ],
                };
            },
        }
    }
</script>
